/**
 * Filters the product image gallery (main slides + thumbnails) to only show
 * the photos that belong to the currently-selected Colour option, similar to
 * On Running's product pages. Also keeps the "Model is X, wearing size Y"
 * note (snippets/product-summary.liquid, .product-model-info) in sync with
 * whichever colour is selected.
 *
 * How it works:
 * - Reads the <script data-product-variants> JSON that the theme already
 *   renders in snippets/product-summary.liquid (product.variants | json),
 *   plus the <script data-variant-media-ids> map added alongside it (variant
 *   id -> variant.featured_media.id). The second script is required because
 *   variant.featured_image.id is a different, legacy Image-ID namespace that
 *   does NOT match product.media / media.id (the ids used for the gallery's
 *   data-media-id attributes) - using featured_image.id here would tag every
 *   colour with ids that never match anything in the DOM and hide the whole
 *   gallery.
 * - Figures out which position (option1/2/3 -> variants[].options[index])
 *   is the "Colour" (or "Color") option by reading the DOM order of the
 *   .variant-field blocks inside .product-variants (same order Shopify
 *   renders product.options_with_values in).
 * - Builds a map of colour value -> set of media ids, using each variant's
 *   id to look up its featured media id in the variant-media-ids map.
 * - Any gallery image that is never referenced by any variant's
 *   featured_image is treated as "unassigned" and is shown as the fallback
 *   set (this covers the original colour that shipped on the product before
 *   any colour-merge work was done, since those variants were never
 *   explicitly tagged with a featured image).
 * - On colour change, hides/shows the matching .main-item / .unity-thumbs-slide
 *   elements via display:none. The main gallery can be a plain grid
 *   (layout-4) or a unity-slider (Embla carousel) - Embla's own built-in
 *   ResizeObserver watches each slide element and automatically reflows /
 *   re-inits itself when a slide's size changes to 0 (display:none), so no
 *   direct calls into the carousel's internals are required.
 * - On the same colour change, also shows/hides the matching
 *   .model-info-entry[data-model-colour] paragraph inside .product-model-info
 *   (rendered per-colour from the custom.model_info product metaobject list),
 *   so the model height / size-worn note always reflects the visible photos.
 *
 * Known limitation: the PhotoSwipe lightbox (product-page.js) builds its
 * image list by querying all ".product-main-slide .media-preview-wrap"
 * buttons at click time, regardless of CSS visibility, so opening the
 * lightbox may still let a shopper swipe through every colour's photos
 * rather than just the selected colour's. The on-page gallery/thumbnails
 * are correctly filtered either way.
 */
(function () {
	"use strict";

	function normalizeOptionName(name) {
		return (name || "").trim().toLowerCase();
	}

	function isColourOptionName(name) {
		var n = normalizeOptionName(name);
		return n === "colour" || n === "color";
	}

	function parseJsonScript(el) {
		if (!el) return null;
		try {
			return JSON.parse(el.textContent);
		} catch (e) {
			return null;
		}
	}

	function getOptionNamesInOrder(variantsContainer) {
		var names = [];
		var fields = variantsContainer.querySelectorAll(":scope > .variant-field");

		fields.forEach(function (field) {
			var radio = field.querySelector('input.field-value[type="radio"]');
			var select = field.querySelector("select.field-value");
			var name = null;

			if (radio) {
				name = radio.getAttribute("name");
			} else if (select) {
				name = select.getAttribute("name");
				if (name) {
					// dropdown fields are rendered as name="[Option Name]"
					name = name.replace(/^\[/, "").replace(/\]$/, "");
				}
			}

			names.push(name);
		});

		return names;
	}

	function buildColourImageMap(variants, colourIndex, variantMediaMap) {
		var map = {};

		variants.forEach(function (variant) {
			if (!variant.options || typeof variant.options[colourIndex] === "undefined") return;

			var colour = variant.options[colourIndex];
			var mediaId = variantMediaMap ? variantMediaMap[variant.id] : null;

			if (mediaId == null) return;

			if (!map[colour]) map[colour] = {};
			map[colour][mediaId] = true;
		});

		return map;
	}

	function collectAllMediaIds(galleryRoot) {
		var ids = {};
		galleryRoot.querySelectorAll("[data-media-id]").forEach(function (el) {
			var id = el.getAttribute("data-media-id");
			if (id) ids[id] = true;
		});
		return ids;
	}

	function applyGalleryFilter(galleryRoot, visibleIds) {
		galleryRoot.querySelectorAll(".main-item[data-media-id], .unity-thumbs-slide[data-media-id]").forEach(function (el) {
			var id = el.getAttribute("data-media-id");
			var shouldShow = !visibleIds || visibleIds[id];

			if (shouldShow) {
				if (el.dataset.colourHidden) {
					el.style.removeProperty("display");
					delete el.dataset.colourHidden;
				}
			} else {
				el.style.display = "none";
				el.dataset.colourHidden = "true";
			}
		});
	}

	function applyModelInfoFilter(modelInfoRoot, colour) {
		if (!modelInfoRoot || !colour) return;

		modelInfoRoot.querySelectorAll(".model-info-entry[data-model-colour]").forEach(function (el) {
			el.hidden = el.getAttribute("data-model-colour") !== colour;
		});
	}

	function initProductSummary(summaryEl) {
		var variantsContainer = summaryEl.querySelector(".product-variants");
		var variantsScript = summaryEl.querySelector("script[data-product-variants]");
		var variantMediaScript = summaryEl.querySelector("script[data-variant-media-ids]");
		var modelInfoRoot = summaryEl.querySelector(".product-model-info");

		if (!variantsContainer || !variantsScript) return;

		var variants = parseJsonScript(variantsScript);
		if (!variants || !variants.length) return;

		var variantMediaMap = parseJsonScript(variantMediaScript) || {};

		var optionNames = getOptionNamesInOrder(variantsContainer);
		var colourIndex = -1;

		for (var i = 0; i < optionNames.length; i++) {
			if (isColourOptionName(optionNames[i])) {
				colourIndex = i;
				break;
			}
		}

		if (colourIndex === -1) return;

		var mainProduct = summaryEl.closest(".main-product");
		var galleryRoot = mainProduct ? mainProduct.querySelector(".product-gallery") : null;

		var colourImageMap = galleryRoot ? buildColourImageMap(variants, colourIndex, variantMediaMap) : {};
		var allMediaIds = galleryRoot ? collectAllMediaIds(galleryRoot) : {};
		var taggedIds = {};

		Object.keys(colourImageMap).forEach(function (colour) {
			Object.keys(colourImageMap[colour]).forEach(function (id) {
				taggedIds[id] = true;
			});
		});

		var unassignedIds = {};
		Object.keys(allMediaIds).forEach(function (id) {
			if (!taggedIds[id]) unassignedIds[id] = true;
		});

		var hasUnassigned = Object.keys(unassignedIds).length > 0;

		function visibleIdsForColour(colour) {
			var forColour = colourImageMap[colour];
			if (forColour && Object.keys(forColour).length) return forColour;
			if (hasUnassigned) return unassignedIds;
			// Nothing tagged for this colour and nothing unassigned to fall
			// back to - show everything rather than an empty gallery.
			return null;
		}

		function applyForColour(colour) {
			if (!colour) return;
			if (galleryRoot) applyGalleryFilter(galleryRoot, visibleIdsForColour(colour));
			if (modelInfoRoot) applyModelInfoFilter(modelInfoRoot, colour);
		}

		var colourOptionName = optionNames[colourIndex];

		function currentColourValue() {
			var checkedRadio = variantsContainer.querySelector(
				'input.field-value[type="radio"][name="' + CSS.escape(colourOptionName) + '"]:checked'
			);
			if (checkedRadio) return checkedRadio.value;

			var select = variantsContainer.querySelector(
				'select.field-value[name="[' + colourOptionName + ']"]'
			);
			if (select) return select.value;

			return null;
		}

		// Apply the filter for whichever colour is selected on load.
		applyForColour(currentColourValue());

		variantsContainer.addEventListener("change", function (e) {
			var target = e.target;
			if (!target) return;

			var isRadio = target.matches && target.matches('input.field-value[type="radio"]');
			var isSelect = target.matches && target.matches("select.field-value");
			if (!isRadio && !isSelect) return;

			var name = target.getAttribute("name") || "";
			name = name.replace(/^\[/, "").replace(/\]$/, "");

			if (!isColourOptionName(name)) return;

			applyForColour(target.value);
		});
	}

	function init(root) {
		root = root || document;
		root.querySelectorAll(".product-summary").forEach(initProductSummary);
	}

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", function () {
			init(document);
		});
	} else {
		init(document);
	}

	// Re-run when the theme editor swaps section markup.
	document.addEventListener("shopify:section:load", function (e) {
		var section = e.target.closest ? e.target.closest("section.shopify-section") : null;
		init(section || document);
	});
})();
